import { IPageRange, ISelectOption } from '@aon/aon-tws-common-ui';
import { IHierarchyVm, INodeSelectionCriteria } from '@aon/tws-common-ui-hierarchy';
import { bubbleUpHierarchyNodeUpdates, collapseChildNodes } from '@aon/tws-common-ui-hierarchy/build/lib/store/HierarchyGraphStore';
import { HashTable, INodeVm } from '@aon/tws-common-ui-hierarchy/build/lib/types';
import _ from 'lodash';
import { Action, Reducer } from 'redux';
import Constants from '../constants';
import ContentConstants from '../contentConstants';
import {
	AudienceSourceType,
	DistributionType,
	ElementType,
	IDisplayLogicValue,
	IPageFlowElementModel,
	IValidationCollection,
	IValidationModel,
	QuestionType,
} from '../models';
import { ActionType } from '../models/displayLogic/ActionType';
import { ConditionOperator } from '../models/displayLogic/ConditionOperator';
import { ConditionSourceType } from '../models/displayLogic/ConditionSourceType';
import { ConditionType } from '../models/displayLogic/ConditionType';
import { DisplayLogicConditionError } from '../models/displayLogic/DisplayLogicConditionError';
import { IDisplayLogicAttribute } from '../models/displayLogic/IDisplayLogicAttribute';
import { IDisplayLogicCondition } from '../models/displayLogic/IDisplayLogicCondition';
import { IDisplayLogicEditModel } from '../models/displayLogic/IDisplayLogicEditModel';
import { IDisplayLogicQuestion } from '../models/displayLogic/IDisplayLogicQuestion';
import { TargetValueOperator } from '../models/displayLogic/TargetValueOperator';
import { TargetValueType } from '../models/displayLogic/TargetValueType';
import { TFetchableEntity } from '../models/IFetchableEntity';
import * as AudienceApi from '../services/AudienceAPI';
import { defaultAudiencePageRange } from '../services/AudienceAPI';
import * as HierarchyAPI from '../services/HierarchyAPI';
import * as PersonAttributeAPI from '../services/PersonAttributeAPI';
import * as QuestionDisplayLogicAPI from '../services/QuestionDisplayLogicAPI';
import { DeriveNew } from '../utilities/Immutable';
import { IApplicationState, TThunkAction } from './';
import { ActionTypes as CommonActionTypes } from './ActionTypes';

const showHideDropDownItems: ISelectOption[] = [
	{
		displayText: 'Show',
		name: '2',
	},
	{
		displayText: 'Hide',
		name: '1',
	},
];

export interface IQuestionDisplayLogicModalState {
	showHideDropDownItems: ISelectOption[];
	questionText: string;
	displayLogic: IDisplayLogicEditModel;
	isOpen: boolean;
	questionDisplayOrder: number;
	unsavedDisplayLogicChanges: boolean;
	showDisplayLogicWarningModal: boolean;
	isLoading: boolean;
	questionHasDisplayLogic: boolean;
	validationResults?: IValidationCollection;
	errorCondition?: DisplayLogicConditionError;
	audienceIds?: number[];
	attributes?: TFetchableEntity<IDisplayLogicAttribute[]>;
	selectableHierarchies?: TFetchableEntity<ISelectOption[]>;
}

export const defaultState: IQuestionDisplayLogicModalState = {
	unsavedDisplayLogicChanges: false,
	questionText: '',
	isOpen: false,
	showHideDropDownItems,
	displayLogic: {
		displayLogicConditions: [],
		displayLogicRule: {
			surveyQuestionId: 0,
			displayLogicAction: {
				actionType: ActionType.ShowQuestion,
			},
		},
	},
	showDisplayLogicWarningModal: false,
	questionDisplayOrder: 0,
	isLoading: false,
	questionHasDisplayLogic: false,
};

export enum ActionTypes {
	QUESTION_DISPLAY_LOGIC_SHOW_HIDE_CHANGE = 'QUESTION_DISPLAY_LOGIC_SHOW_HIDE_CHANGE',
	QUESTION_DISPLAY_LOGIC_RESET = 'QUESTION_DISPLAY_LOGIC_RESET',
	QUESTION_DISPLAY_LOGIC_WARNING_MODAL_CLOSE = 'QUESTION_DISPLAY_LOGIC_WARNING_MODAL_CLOSE',
	QUESTION_DISPLAY_LOGIC_WARNING_MODAL_SUBMIT = 'QUESTION_DISPLAY_LOGIC_WARNING_MODAL_SUBMIT',
	QUESTION_DISPLAY_LOGIC_MODAL_TOGGLE = 'QUESTION_DISPLAY_LOGIC_MODAL_TOGGLE',
	QUESTION_DISPLAY_LOGIC_ADD_NEW_QUESTION_CONDITION = 'QUESTION_DISPLAY_LOGIC_ADD_NEW_QUESTION_CONDITION',
	QUESTION_DISPLAY_LOGIC_ADD_HIERARCHY_CONDITION = 'QUESTION_DISPLAY_LOGIC_ADD_HIERARCHY_CONDITION',
	QUESTION_DISPLAY_LOGIC_WARNING_MODAL_OPEN = 'QUESTION_DISPLAY_LOGIC_WARNING_MODAL_OPEN',
	QUESTION_DISPLAY_LOGIC_QUESTION_CHANGE = 'QUESTION_DISPLAY_LOGIC_QUESTION_CHANGE',
	QUESTION_DISPLAY_LOGIC_RESPONSE_CHANGE = 'QUESTION_DISPLAY_LOGIC_RESPONSE_CHANGE',
	QUESTION_DISPLAY_LOGIC_DELETE = 'QUESTION_DISPLAY_LOGIC_DELETE',
	QUESTION_DISPLAY_LOGIC_SAVE_REQUESTED = 'QUESTION_DISPLAY_LOGIC_SAVE_REQUESTED',
	QUESTION_DISPLAY_LOGIC_SAVE_SUCCEEDED = 'QUESTION_DISPLAY_LOGIC_SAVE_SUCCEEDED',
	QUESTION_DISPLAY_LOGIC_SAVE_FAILED = 'QUESTION_DISPLAY_LOGIC_SAVE_FAILED',
	QUESTION_DISPLAY_LOGIC_GET_REQUESTED = 'QUESTION_DISPLAY_LOGIC_GET_REQUESTED',
	QUESTION_DISPLAY_LOGIC_GET_SUCCEEDED = 'QUESTION_DISPLAY_LOGIC_GET_SUCCEEDED',
	QUESTION_DISPLAY_LOGIC_GET_FAILED = 'QUESTION_DISPLAY_LOGIC_GET_FAILED',
	QUESTION_DISPLAY_LOGIC_ADD_NEW_ATTRIBUTE_CONDITION = 'QUESTION_DISPLAY_LOGIC_ADD_NEW_ATTRIBUTE_CONDITION',
	QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES_BEGIN = 'QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES_BEGIN',
	QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES = 'QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES',
	QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES_FAILED = 'QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES_FAILED',
	QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTE_VALUES_BEGIN = 'QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTE_VALUES_BEGIN',
	QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTE_VALUES = 'QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTE_VALUES',
	QUESTION_DISPLAY_LOGIC_HANDLE_ATTRIBUTE_CHANGE = 'QUESTION_DISPLAY_LOGIC_HANDLE_ATTRIBUTE_CHANGE',
	QUESTION_DISPLAY_LOGIC_ADD_CONDITION_ERROR = 'QUESTION_DISPLAY_LOGIC_ADD_CONDITION_ERROR',
	QUESTION_DISPLAY_LOGIC_GET_AUDIENCE_ID = 'QUESTION_DISPLAY_LOGIC_GET_AUDIENCE_ID',
	QUESTION_DISPLAY_LOGIC_HANDLE_SELECTION_DISMISSAL = 'QUESTION_DISPLAY_LOGIC_HANDLE_SELECTION_DISMISSAL',
	QUESTION_DISPLAY_LOGIC_HIERARCHY_CHANGE = 'QUESTION_DISPLAY_LOGIC_HIERARCHY_CHANGE',
	QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_REQUESTED = 'QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_REQUESTED',
	QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_SUCCEEDED = 'QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_SUCCEEDED',
	QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_FAILED = 'QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_FAILED',
	QUESTION_DISPLAY_LOGIC_NODE_SELECTION_CHANGED = 'QUESTION_DISPLAY_LOGIC_NODE_SELECTION_CHANGED',
	QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_REQUESTED = 'QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_REQUESTED',
	QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_SUCCEEDED = 'QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_SUCCEEDED',
	QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_FAILED = 'QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_FAILED',

	// Hierarchy
	QUESTION_DISPLAY_LOGIC_NODE_TOGGLE = 'QUESTION_DISPLAY_LOGIC_NODE_TOGGLE',
	QUESTION_DISPLAY_LOGIC_NODES_DICT_UPDATE = 'QUESTION_DISPLAY_LOGIC_NODES_DICT_UPDATE',
	QUESTION_DISPLAY_LOGIC_INITIAL_LOAD_FAILED = 'QUESTION_DISPLAY_LOGIC_INITIAL_LOAD_FAILED',
	QUESTION_DISPLAY_LOGIC_SELECT_NODE = 'QUESTION_DISPLAY_LOGIC_SELECT_NODE',
	QUESTION_DISPLAY_LOGIC_RESET_PENDING_SCROLL = 'QUESTION_DISPLAY_LOGIC_RESET_PENDING_SCROLL',
	QUESTION_DISPLAY_LOGIC_HIDE_BELONGS_TO_NODES = 'QUESTION_DISPLAY_LOGIC_HIDE_BELONGS_TO_NODES',
	QUESTION_DISPLAY_LOGIC_SHOW_BELONGS_TO_NODES = 'QUESTION_DISPLAY_LOGIC_SHOW_BELONGS_TO_NODES',
	QUESTION_DISPLAY_LOGIC_LOAD_MORE_BELONGS_TO_NODES = 'QUESTION_DISPLAY_LOGIC_LOAD_MORE_BELONGS_TO_NODES',
	QUESTION_DISPLAY_LOGIC_LOAD_MORE_TOP_LEVEL_ORPHAN_NODES = 'QUESTION_DISPLAY_LOGIC_LOAD_MORE_TOP_LEVEL_ORPHAN_NODES',
	QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_INIT = 'QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_INIT',
	QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_COMPLETE = 'QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_COMPLETE',
	QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_FAILED = 'QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_FAILED',

	// Page Breaks
	PAGEFLOW_ELEMENT_MODAL_SUBMIT = 'PAGEFLOW_ELEMENT_MODAL_SUBMIT',
	SURVEYELEMENT_DELETE = 'SURVEYELEMENT_DELETE',
}

export type TActionCreator<R> = TThunkAction<R, KnownActions | { type: CommonActionTypes }>;

export const ActionCreators = {
	ToggleDisplayLogicModal: (isOpen: boolean, surveyQuestionId: number): TActionCreator<void> => {
		return async (dispatch, getState) => {
			const {
				survey: {
					surveyEditModel: {
						survey: { surveyId },
						questions,
					},
				},
			} = getState();
			const question = questions.find((q) => q.surveyQuestionId === surveyQuestionId)!;
			const questionText = question.text;
			const questionDisplayOrder = question.sequence!;
			if (isOpen) {
				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_REQUESTED });

				try {
					// Fetch display logic data
					const result = await QuestionDisplayLogicAPI.GetQuestionDisplayLogic(surveyId, surveyQuestionId);
					const { displayLogicRule, displayLogicConditions } = result[surveyQuestionId]?.[0] ?? defaultState.displayLogic;

					// Dispatch to bind display logic data to state
					dispatch({
						type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_SUCCEEDED,
						payload: {
							displayLogicRule,
							displayLogicConditions: displayLogicConditions.map((c, index) => ({
								...c,

								index,
								questions: getFilteredQuestionsFromState(getState, displayLogicRule.surveyQuestionId),
								attributes: [],
							})),
						},
					});

					// Dispatch to toggle the modal on
					dispatch({
						type: ActionTypes.QUESTION_DISPLAY_LOGIC_MODAL_TOGGLE,
						payload: { isOpen, surveyQuestionId, questionText, questionDisplayOrder },
					});

					// If the survey is linked to a source type
					const sourceType = getState().survey.surveyEditModel.audienceSource.audienceSourceType;
					if (sourceType) {
						// Populate the state with source info
						dispatch(ActionCreators.PopulateSourceInfo(sourceType));
					}
				} catch (error) {
					// Dispatch to notify that display logic data could not be derived
					dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_FAILED, payload: (error as Error).message });
				}
			} else {
				dispatch({
					type: ActionTypes.QUESTION_DISPLAY_LOGIC_MODAL_TOGGLE,
					payload: { isOpen, surveyQuestionId, questionText, questionDisplayOrder },
				});
			}
		};
	},
	HandleShowHideChange: (values?: string | string[] | undefined): TActionCreator<void> => {
		return (dispatch) => {
			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_SHOW_HIDE_CHANGE, payload: values as unknown as ActionType });
		};
	},
	ResetQuestionDisplayLogic: (): TActionCreator<void> => {
		return (dispatch) => {
			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_RESET });
		};
	},
	CloseDisplayLogicWarningModal: (): TActionCreator<void> => {
		return (dispatch) => {
			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_WARNING_MODAL_CLOSE });
		};
	},
	SubmitDisplayLogicWarningModal: (): TActionCreator<void> => {
		return (dispatch) => {
			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_WARNING_MODAL_SUBMIT });
		};
	},
	HandleQuestionChange(surveyQuestionId: number, questionText: string, index: number): TActionCreator<void> {
		return (dispatch) => {
			dispatch({
				type: ActionTypes.QUESTION_DISPLAY_LOGIC_QUESTION_CHANGE,
				payload: {
					surveyQuestionId,
					questionText,
					index,
				},
			});
		};
	},
	HandleHierarchyNodeSelection: (selectionCriteria: INodeSelectionCriteria, index: number): TActionCreator<void> => {
		return (dispatch, getState) => {
			let node = JSON.stringify(selectionCriteria);
			if (node === JSON.stringify({ descendantsIncluded: {}, singleIncluded: {} } as INodeSelectionCriteria)) {
				node = '';
			}
			const values = getState().questionDisplayLogic.displayLogic.displayLogicConditions[index].displayLogicValues;
			if (values.length === 0 || (values.length > 0 && node !== values[0].valueText!)) {
				dispatch({
					type: ActionTypes.QUESTION_DISPLAY_LOGIC_NODE_SELECTION_CHANGED,
					payload: {
						index,
						node,
					},
				});
			}
		};
	},
	HandleHierarchyChange(hierarchyMetadataId: string, name: string, index: number, values: IDisplayLogicValue[]): TActionCreator<void> {
		return async (dispatch) => {
			// Dispatch to inform of the hierarchy change
			dispatch({
				type: ActionTypes.QUESTION_DISPLAY_LOGIC_HIERARCHY_CHANGE,
				payload: {
					hierarchyMetadataId,
					name,
					index,
					displayLogicValues: values,
				},
			});

			// === Fetch info for the hierarchy selected === //
			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_REQUESTED, payload: index });

			try {
				const hierarchy = await HierarchyAPI.getHierarchy({ hierarchyId: hierarchyMetadataId, includeOwners: false });

				dispatch({
					type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_SUCCEEDED,
					payload: {
						hierarchyVm: hierarchy,
						index,
					},
				});
			} catch {
				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_FAILED, payload: index });
			}
		};
	},
	HandleAttributeChange(columnIndex: number, attributeText: string, conditionIndex: number): TActionCreator<void> {
		return (dispatch) => {
			dispatch(ActionCreators.FetchAttributeValues(attributeText, conditionIndex));
			dispatch({
				type: ActionTypes.QUESTION_DISPLAY_LOGIC_QUESTION_CHANGE,
				payload: {
					surveyQuestionId: columnIndex,
					questionText: attributeText,
					index: conditionIndex,
				},
			});
		};
	},
	HandleResponseChange(responses: string[], index: number): TActionCreator<void> {
		return (dispatch) => {
			dispatch({
				type: ActionTypes.QUESTION_DISPLAY_LOGIC_RESPONSE_CHANGE,
				payload: {
					responses,
					index,
				},
			});
		};
	},
	OnConditionPreviousQuestionSelect: (): TActionCreator<void> => {
		return (dispatch, getState) => {
			const payload: IDisplayLogicQuestion[] = getFilteredQuestionsFromState(getState);
			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_NEW_QUESTION_CONDITION, payload });
		};
	},
	OnConditionAttributeSelect: (): TActionCreator<void> => {
		return (dispatch, getState) => {
			return getErrorCondition(getState, false).then((error) => {
				if (error === undefined) {
					const filteredAttributes = getUnusedAttributes(getState().questionDisplayLogic);
					const sourceType = getState().survey.surveyEditModel.survey.audienceSourceType;
					const conditionSourceType =
						sourceType === AudienceSourceType.Hris ? ConditionSourceType.HrisAttribute : ConditionSourceType.FileAttribute;
					dispatch({
						type: ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_NEW_ATTRIBUTE_CONDITION,
						payload: { attributes: filteredAttributes, sourceType: conditionSourceType },
					});
				} else {
					dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_CONDITION_ERROR, payload: error });
				}
			});
		};
	},
	OnConditionHierarchySelect: (): TActionCreator<void> => {
		return (dispatch, getState) => {
			return getErrorCondition(getState, true).then((error) => {
				if (error === undefined) {
					dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_HIERARCHY_CONDITION });
				} else {
					dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_CONDITION_ERROR, payload: error });
				}
			});
		};
	},
	SaveQuestionDisplayLogic: (): TActionCreator<void> => {
		return (dispatch, getState) => {
			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_SAVE_REQUESTED });
			const {
				survey: {
					surveyEditModel: {
						survey: { surveyId },
					},
				},
				questionDisplayLogic: { displayLogic },
			} = getState();
			const surveyQuestionId = displayLogic.displayLogicRule.surveyQuestionId;
			return QuestionDisplayLogicAPI.UpdateQuestionDisplayLogic(surveyId, {
				[surveyQuestionId]: [displayLogic],
			})
				.then(() => {
					dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_SAVE_SUCCEEDED });
					dispatch({ type: CommonActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
					dispatch({
						type: CommonActionTypes.SURVEY_QUESTION_DISPLAY_RULES_COUNT_UPDATE,
						payload: {
							surveyQuestionId,
							displayRulesCount: displayLogic.displayLogicConditions.length > 0 ? 1 : 0,
							operation: 'set',
						},
					});
				})
				.catch((error) => {
					let payload: IValidationCollection | undefined;
					if (error.response) {
						const editModels = error.response.data[
							displayLogic.displayLogicRule.surveyQuestionId
						] as IValidationModel<IDisplayLogicEditModel>[];
						if (editModels.length > 0 && !editModels[0].isValid) {
							payload = editModels[0].validationResults;
						}
					}
					dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_SAVE_FAILED, payload });
					dispatch({
						type: CommonActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
						payload: {
							message: ContentConstants.TOAST_ERROR_GENERIC_BODY,
							messageTitle: ContentConstants.TOAST_ERROR_GENERIC_TITLE,
						},
					});
				});
		};
	},
	CloseQuestionDisplayLogic: (): TActionCreator<void> => {
		return (dispatch, getState) => {
			if (getState().questionDisplayLogic.unsavedDisplayLogicChanges) {
				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_WARNING_MODAL_OPEN });
			} else {
				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_RESET });
			}
		};
	},
	DeleteDisplayLogic: (index: number): TActionCreator<void> => {
		return (dispatch) => {
			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_DELETE, payload: index });
		};
	},
	PopulateSourceInfo: (sourceType: AudienceSourceType): TActionCreator<void> => {
		return async (dispatch, stateOf) => {
			const { survey, questionDisplayLogic } = stateOf();

			const { displayLogicConditions: displayConditions } = questionDisplayLogic.displayLogic;
			const { externalHrisId: hrisId } = survey.surveyEditModel.audienceSource;

			// ======================= Fetch hierarchy options ======================= //
			if ([AudienceSourceType.Hris, AudienceSourceType.Hierarchy].includes(sourceType) && hrisId) {
				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_REQUESTED });

				try {
					const hierarchies = await HierarchyAPI.getHrisHierarchies(hrisId);

					dispatch({
						type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_SUCCEEDED,
						payload: hierarchies.map((h) => ({ name: h.hierarchyId, displayText: h.name })),
					});
				} catch {
					dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_FAILED });
				}
			}

			// ======================= Fetch attributes ======================= //
			await dispatch(ActionCreators.FetchAttributes());

			displayConditions.forEach((cond, idx) => {
				// ======================= For conditions predicated on an attribute selection, populate with attr values ======================= //
				if (
					((sourceType === AudienceSourceType.Hris && cond.conditionSourceType === ConditionSourceType.HrisAttribute) ||
						sourceType === AudienceSourceType.FileUpload) &&
					cond.conditionSourceText
				) {
					dispatch(ActionCreators.FetchAttributeValues(cond.conditionSourceText, idx));
				}

				// ======================= For conditions predicated on hierarchy relationships, fetch hierarchy graph info ======================= //
				if (
					sourceType === AudienceSourceType.Hierarchy ||
					(sourceType === AudienceSourceType.Hris && cond.conditionSourceType === ConditionSourceType.HierarchyNode)
				) {
					dispatch(ActionCreators.HandleHierarchyChange(cond.conditionSourceId!, cond.conditionSourceText!, idx, cond.displayLogicValues));
				}
			});
		};
	},
	FetchAttributes: (): TActionCreator<Promise<void>> => {
		return async (dispatch, stateOf) => {
			const { survey } = stateOf();
			const { audienceSourceType, externalHrisId: hrisId } = survey.surveyEditModel.audienceSource;

			const deriveAttributes = async (): Promise<IDisplayLogicAttribute[]> => {
				if (audienceSourceType === AudienceSourceType.Hris) {
					if (!hrisId) {
						throw new Error(ContentConstants.DISPLAY_LOGIC_CONDITION_ERROR_NO_HRIS_ID);
					}

					return (await HierarchyAPI.getHrisAttributes(hrisId)).map((attr) => ({ ...attr, displayName: attr.name, values: [] }));
				}

				if (audienceSourceType === AudienceSourceType.FileUpload) {
					const { clientId, surveyId } = survey.surveyEditModel.survey;

					const pagedAudiences = await AudienceApi.getPagedAudiences(surveyId, defaultAudiencePageRange);
					const attributesForAud = await Promise.all(
						pagedAudiences.items.map((aud) => PersonAttributeAPI.getPersonAttributes(surveyId, aud.audienceId, clientId)),
					);

					const attributes = attributesForAud.reduce((displayLogicAttrs, personAttrs) => {
						personAttrs.forEach((attr) => {
							const displayLogicAttr: IDisplayLogicAttribute = {
								name: attr.attributeName,
								displayName: attr.attributeDisplayName ?? '',
								type: 'string',
								columnIndex: attr.attributeId,
								values: [],
							};

							displayLogicAttrs.set(attr.attributeId.toString(), displayLogicAttr);
						});

						return displayLogicAttrs;
					}, new Map<string, IDisplayLogicAttribute>());

					return Array.from(attributes.values());
				}

				return [];
			};

			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES_BEGIN });

			try {
				const attributes = await deriveAttributes();

				dispatch({
					type: ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES,
					payload: attributes,
				});
			} catch {
				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES_FAILED });
			}
		};
	},
	FetchAttributeValues: (forAttribute: string, conditionIndex: number): TActionCreator<Promise<void>> => {
		return async (dispatch, stateOf) => {
			const { survey } = stateOf();
			const { audienceSourceType, externalHrisId: hrisId } = survey.surveyEditModel.audienceSource;

			const deriveAttributeValues = async (): Promise<string[]> => {
				if (audienceSourceType === AudienceSourceType.Hris) {
					if (!hrisId) {
						throw new Error(ContentConstants.DISPLAY_LOGIC_CONDITION_ERROR_NO_HRIS_ID);
					}
					const attrValues = await HierarchyAPI.getHrisAttributeValues(hrisId, forAttribute);
					return attrValues.map((a) => a.value).filter((v): v is string => !!v);
				}

				if (audienceSourceType === AudienceSourceType.FileUpload) {
					const { clientId, surveyId } = survey.surveyEditModel.survey;

					const pagedAudiences = await AudienceApi.getPagedAudiences(surveyId, defaultAudiencePageRange);
					const attributesForAud = await Promise.all(
						pagedAudiences.items.map((aud) => PersonAttributeAPI.getPersonAttributes(surveyId, aud.audienceId, clientId)),
					);

					const attrValueSet = attributesForAud.reduce((attrValues, attrs) => {
						attrs.forEach((attr) => {
							if (attr.attributeName === forAttribute && attr.value) {
								attrValues.add(attr.value);
							}
						});

						return attrValues;
					}, new Set<string>());

					return Array.from(attrValueSet);
				}

				return [];
			};

			try {
				const attrValues = await deriveAttributeValues();

				dispatch({
					type: ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTE_VALUES,
					payload: { name: forAttribute, values: attrValues, conditionIndex },
				});
			} catch {
				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES_FAILED });
			}
		};
	},
	HandleSelectionDismissal: (columnIndex: number, values: ISelectOption[]): TActionCreator<void> => {
		return (dispatch) => {
			dispatch({
				type: ActionTypes.QUESTION_DISPLAY_LOGIC_HANDLE_SELECTION_DISMISSAL,
				payload: { columnIndex, values },
			});
		};
	},

	// Hierarchy graph store copied from common hierarchy
	toggleNode: (nodeId: string, isExpanded: boolean, index: number): TActionCreator<void> => {
		return (dispatch, getState) => {
			// if it's expanded than we just have to collapse it
			const { hierarchy } = getState().questionDisplayLogic.displayLogic.displayLogicConditions[index];

			if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData) {
				return;
			}

			if (isExpanded || isNodeFullyLoaded(hierarchy, nodeId)) {
				return dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_NODE_TOGGLE, payload: { nodeId, index } });
			}

			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_NODE_TOGGLE, payload: { nodeId, index } });
			return dispatch(ActionCreators.fetchNode(nodeId, index));
		};
	},
	fetchNode: (nodeId: string, index: number, expandPath: boolean | undefined = false): TActionCreator<void> => {
		return async (dispatch, getState) => {
			if (nodeId === Constants.ORPHAN_PLACEHOLDER_ID) {
				return;
			}

			const { hierarchy } = getState().questionDisplayLogic.displayLogic.displayLogicConditions[index];

			if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData) {
				return;
			}

			const hierarchyId = getHierarchyMetadataId(hierarchy);

			try {
				const hierarchyVm = await HierarchyAPI.getHierarchy({ hierarchyId, nodeId, expandPath });

				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_NODES_DICT_UPDATE, payload: { nodeId, hierarchyVm, expandPath, index } });

				if (expandPath) {
					dispatch(ActionCreators.selectNode(nodeId, index));
				}
			} catch {
				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_INITIAL_LOAD_FAILED, payload: { index } });
			}
		};
	},
	selectNode: (nodeId: string, index: number): TActionCreator<void> => {
		return (dispatch) => {
			if (nodeId === Constants.ORPHAN_PLACEHOLDER_ID) {
				return;
			}

			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_SELECT_NODE, payload: { nodeId, index } });
		};
	},
	toggleShowBelongsToNodes: (nodeId: string, index: number): TActionCreator<void> => {
		return async (dispatch, getState) => {
			const { hierarchy } = getState().questionDisplayLogic.displayLogic.displayLogicConditions[index];

			if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData) {
				return;
			}

			const node = hierarchy.nodes![nodeId];
			if (node.showBelongsToNodes) {
				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_HIDE_BELONGS_TO_NODES, payload: { nodeId, index } });
			} else if (node.belongsToNodeIds && node.belongsToNodeIds.length > 0) {
				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_SHOW_BELONGS_TO_NODES, payload: { nodeId, index } });
			} else {
				const hierarchyId = getHierarchyMetadataId(hierarchy);

				const nodes = await HierarchyAPI.fetchNodesWithRelationship('Belongs To', nodeId, hierarchyId);

				return dispatch({
					type: ActionTypes.QUESTION_DISPLAY_LOGIC_SHOW_BELONGS_TO_NODES,
					payload: { nodeId, nodes: nodes.items, pageRange: nodes.pageRange, index },
				});
			}
		};
	},
	loadMoreBelongsToNodes: (nodeId: string, index: number): TActionCreator<void> => {
		return async (dispatch, getState) => {
			const { hierarchy } = getState().questionDisplayLogic.displayLogic.displayLogicConditions[index];

			if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData) {
				return;
			}

			const node = hierarchy.nodes![nodeId];
			const { offset, limit } = node.belongsToPageRange!;
			const hierarchyId = getHierarchyMetadataId(hierarchy);

			const nodes = await HierarchyAPI.fetchNodesWithRelationship('Belongs To', nodeId, hierarchyId, offset, limit);

			return dispatch({
				type: ActionTypes.QUESTION_DISPLAY_LOGIC_LOAD_MORE_BELONGS_TO_NODES,
				payload: { nodeId, nodes: nodes.items, pageRange: nodes.pageRange, index },
			});
		};
	},
	loadMoreTopLevelOrphanNodes: (index: number): TActionCreator<void> => {
		return async (dispatch, getState) => {
			const { hierarchy } = getState().questionDisplayLogic.displayLogic.displayLogicConditions[index];

			if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData) {
				return;
			}

			const { offset, limit } = hierarchy.orphanedDependentNodeIds!.pageRange;
			const hierarchyId = getHierarchyMetadataId(hierarchy);

			const nodes = await HierarchyAPI.fetchOrphanNodes('Person', hierarchyId, offset + limit, offset);

			return dispatch({
				type: ActionTypes.QUESTION_DISPLAY_LOGIC_LOAD_MORE_TOP_LEVEL_ORPHAN_NODES,
				payload: { nodes: nodes.items, pageRange: nodes.pageRange, index },
			});
		};
	},
	resetPendingScroll: (index: number): TActionCreator<void> => {
		return (dispatch) => {
			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_RESET_PENDING_SCROLL, payload: { index } });
		};
	},
	fetchNodAncestors: (hierarchyNodeIds: string[], index: number): TActionCreator<void> => {
		return async (dispatch, getState) => {
			const { hierarchy } = getState().questionDisplayLogic.displayLogic.displayLogicConditions[index];

			if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData) {
				return;
			}

			const hierarchyId = getHierarchyMetadataId(hierarchy);

			dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_INIT, payload: { index } });

			try {
				const hierarchyVm = await HierarchyAPI.fetchAncestorOf(hierarchyNodeIds, hierarchyId);

				dispatch({
					type: ActionTypes.QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_COMPLETE,
					payload: { hierarchyNodeIds, hierarchyVm, index },
				});
			} catch {
				dispatch({ type: ActionTypes.QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_FAILED, payload: { index } });
			}
		};
	},
};

export interface IQuestionDisplayLogicToggleNode extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_NODE_TOGGLE;
	payload: { nodeId: string; index: number };
}

export interface IQuestionDisplayLogicNodeDictUpdate extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_NODES_DICT_UPDATE;
	payload: { nodeId: string; index: number; hierarchyVm: IHierarchyVm; expandPath?: boolean };
}

export interface IQuestionDisplayLogicSelectNode extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_SELECT_NODE;
	payload: { nodeId: string; index: number };
}

export interface IQuestionDisplayLogicHideBelongsToNodes extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_HIDE_BELONGS_TO_NODES;
	payload: { nodeId: string; index: number };
}

export interface IQuestionDisplayLogicShowBelongsToNodes extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_SHOW_BELONGS_TO_NODES;
	payload: { nodeId: string; index: number; nodes?: INodeVm[]; pageRange?: IPageRange };
}

export interface IQuestionDisplayLogicLoadMoreBelongsToNodes extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_LOAD_MORE_BELONGS_TO_NODES;
	payload: { nodeId: string; index: number; nodes?: INodeVm[]; pageRange: IPageRange };
}

export interface IQuestionDisplayLogicBeginFetchingNodeAncestors extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_INIT;
	payload: { index: number };
}

export interface IQuestionDisplayLogicFetchNodeAncestorsFailed extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_FAILED;
	payload: { index: number };
}

export interface IQuestionDisplayLogicLoadMoreTopLevelOrphanNodes extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_LOAD_MORE_TOP_LEVEL_ORPHAN_NODES;
	payload: { index: number; nodes: INodeVm[]; pageRange: IPageRange };
}

export interface IQuestionDisplayLogicHierarchyFailed extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_INITIAL_LOAD_FAILED;
	payload: { index: number };
}

export interface IQuestionDisplayLogicHierarchyResetPendingScroll extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_RESET_PENDING_SCROLL;
	payload: { index: number };
}

export interface IQuestionDisplayLogicShowHideChanged extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_SHOW_HIDE_CHANGE;
	payload: ActionType;
}

export interface IQuestionDisplayLogicHierarchyVMGetRequested extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_REQUESTED;
	payload: number;
}
export interface IQuestionDisplayLogicHierarchyVMGetSucceeded extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_SUCCEEDED;
	payload: {
		hierarchyVm: IHierarchyVm;
		index: number;
	};
}
export interface IQuestionDisplayLogicHierarchyVMGetFailed extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_FAILED;
	payload: number;
}

export interface IQuestionDisplayLogicReset extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_RESET;
}

export interface IQuestionDisplayLogicWarningModalOpen extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_WARNING_MODAL_OPEN;
}

export interface IQuestionDisplayLogicWarningModalClose extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_WARNING_MODAL_CLOSE;
}

export interface IQuestionDisplayLogicWarningModalSubmit extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_WARNING_MODAL_SUBMIT;
}
export interface IQuestionDisplayLogicAddQuestionCondition extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_NEW_QUESTION_CONDITION;
	payload: IDisplayLogicQuestion[];
}
export interface IQuestionDisplayLogicAddHierarchyCondition extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_HIERARCHY_CONDITION;
}

export interface IQuestionDisplayLogicGetHierarchiesRequested extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_REQUESTED;
}
export interface IQuestionDisplayLogicGetHierarchiesSucceeded extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_SUCCEEDED;
	payload: ISelectOption[];
}

export interface IQuestionDisplayLogicGetHierarchiesFailed extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_FAILED;
}

export interface IQuestionDisplayLogicNodeSelectionChanged extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_NODE_SELECTION_CHANGED;
	payload: {
		node: string;
		index: number;
	};
}
export interface IQuestionDisplayLogicAddAttributeCondition extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_NEW_ATTRIBUTE_CONDITION;
	payload: { attributes: IDisplayLogicAttribute[]; sourceType: ConditionSourceType };
}

export interface IQuestionDisplayLogicToggleModal extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_MODAL_TOGGLE;
	payload: {
		isOpen: boolean;
		surveyQuestionId: number;
		questionText: string;
		questionDisplayOrder: number;
	};
}

export interface IQuestionDisplayQuestionChange extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_QUESTION_CHANGE;
	payload: {
		surveyQuestionId: number;
		questionText: string;
		index: number;
	};
}
export interface IQuestionDisplayHierarchyChange extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_HIERARCHY_CHANGE;
	payload: {
		hierarchyMetadataId: string;
		name: string;
		index: number;
		displayLogicValues: IDisplayLogicValue[];
	};
}

export interface IQuestionDisplayResponseChange extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_RESPONSE_CHANGE;
	payload: {
		responses: string[];
		index: number;
	};
}

export interface IQuestionDisplayLogicDelete extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_DELETE;
	payload: number;
}

export interface IQuestionDisplaySaveRequested extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_SAVE_REQUESTED;
}

export interface IQuestionDisplaySaveSucceeded extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_SAVE_SUCCEEDED;
}

export interface IQuestionDisplaySaveFailed extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_SAVE_FAILED;
	payload: IValidationCollection | undefined;
}

export interface IQuestionDisplayGetRequested extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_REQUESTED;
}

export interface IQuestionDisplayGetSucceeded extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_SUCCEEDED;
	payload: IDisplayLogicEditModel;
}

export interface IQuestionDisplayGetFailed extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_GET_FAILED;
	payload: string;
}

export interface IQuestionDisplayBeginFetchingAttributes extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES_BEGIN;
}

export interface IQuestionDisplayFetchAttributes extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES;
	payload: IDisplayLogicAttribute[];
}

export interface IQuestionDisplayFetchAttributesFailed extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES_FAILED;
}

export interface IQuestionDisplayFetchAttributeValues extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTE_VALUES;
	payload: { name: string; values: string[]; conditionIndex: number };
}

export interface IQuestionDisplayLogicConditionError extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_CONDITION_ERROR;
	payload: DisplayLogicConditionError;
}

export interface IQuestionDisplayLogicHandleValueDismissal extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_HANDLE_SELECTION_DISMISSAL;
	payload: { columnIndex: number; values: ISelectOption[] };
}

export interface IQuestionDisplayLogicSelectNodeUpdate extends Action {
	type: ActionTypes.QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_COMPLETE;
	payload: { hierarchyVm: IHierarchyVm; hierarchyNodeIds: string[]; index: number };
}

export type KnownActions =
	| IQuestionDisplayLogicShowHideChanged
	| IQuestionDisplayLogicWarningModalClose
	| IQuestionDisplayLogicWarningModalOpen
	| IQuestionDisplayLogicWarningModalSubmit
	| IQuestionDisplayLogicToggleModal
	| IQuestionDisplayLogicAddQuestionCondition
	| IQuestionDisplayQuestionChange
	| IQuestionDisplayResponseChange
	| IQuestionDisplayLogicDelete
	| IQuestionDisplayLogicReset
	| IQuestionDisplaySaveRequested
	| IQuestionDisplaySaveSucceeded
	| IQuestionDisplaySaveFailed
	| IQuestionDisplayGetRequested
	| IQuestionDisplayGetSucceeded
	| IQuestionDisplayGetFailed
	| IQuestionDisplayLogicAddAttributeCondition
	| IQuestionDisplayBeginFetchingAttributes
	| IQuestionDisplayFetchAttributes
	| IQuestionDisplayFetchAttributesFailed
	| IQuestionDisplayFetchAttributeValues
	| IQuestionDisplayLogicConditionError
	| IQuestionDisplayLogicAddHierarchyCondition
	| IQuestionDisplayHierarchyChange
	| IQuestionDisplayLogicHierarchyVMGetRequested
	| IQuestionDisplayLogicHierarchyVMGetSucceeded
	| IQuestionDisplayLogicHierarchyVMGetFailed
	| IQuestionDisplayLogicNodeSelectionChanged
	| IQuestionDisplayLogicHandleValueDismissal
	| IQuestionDisplayLogicGetHierarchiesRequested
	| IQuestionDisplayLogicGetHierarchiesSucceeded
	| IQuestionDisplayLogicGetHierarchiesFailed
	| IQuestionDisplayLogicToggleNode
	| IQuestionDisplayLogicNodeDictUpdate
	| IQuestionDisplayLogicSelectNode
	| IQuestionDisplayLogicHideBelongsToNodes
	| IQuestionDisplayLogicShowBelongsToNodes
	| IQuestionDisplayLogicLoadMoreBelongsToNodes
	| IQuestionDisplayLogicLoadMoreTopLevelOrphanNodes
	| IQuestionDisplayLogicHierarchyFailed
	| IQuestionDisplayLogicHierarchyResetPendingScroll
	| IQuestionDisplayLogicBeginFetchingNodeAncestors
	| IQuestionDisplayLogicSelectNodeUpdate
	| IQuestionDisplayLogicFetchNodeAncestorsFailed;

export const reducer: Reducer<IQuestionDisplayLogicModalState, KnownActions> = (
	state: IQuestionDisplayLogicModalState | undefined,
	action: KnownActions,
): IQuestionDisplayLogicModalState => {
	if (!state) {
		return defaultState;
	}

	switch (action.type) {
		case ActionTypes.QUESTION_DISPLAY_LOGIC_WARNING_MODAL_SUBMIT:
		case ActionTypes.QUESTION_DISPLAY_LOGIC_SAVE_SUCCEEDED:
		case ActionTypes.QUESTION_DISPLAY_LOGIC_RESET:
			return { ...defaultState };
		case ActionTypes.QUESTION_DISPLAY_LOGIC_SAVE_FAILED:
			return {
				...state,
				validationResults: action.payload,
			};
		case ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_CONDITION_ERROR:
			return {
				...state,
				errorCondition: action.payload,
			};
		case ActionTypes.QUESTION_DISPLAY_LOGIC_GET_REQUESTED:
			return {
				...state,
				isLoading: true,
			};
		case ActionTypes.QUESTION_DISPLAY_LOGIC_GET_FAILED:
			return {
				...state,
				isLoading: false,
			};
		case ActionTypes.QUESTION_DISPLAY_LOGIC_GET_SUCCEEDED:
			return {
				...state,
				isLoading: false,
				displayLogic: action.payload.displayLogicConditions.length !== 0 ? action.payload : state.displayLogic,
				questionHasDisplayLogic: action.payload.displayLogicConditions.length !== 0,
			};
		case ActionTypes.QUESTION_DISPLAY_LOGIC_MODAL_TOGGLE:
			return {
				...state,
				isOpen: action.payload.isOpen,
				displayLogic: {
					...state.displayLogic,
					displayLogicRule: {
						...state.displayLogic.displayLogicRule,
						surveyQuestionId: action.payload.surveyQuestionId,
						conditionOperator: ConditionOperator.And,
					},
				},
				questionText: action.payload.questionText,
				questionDisplayOrder: action.payload.questionDisplayOrder,
			};
		case ActionTypes.QUESTION_DISPLAY_LOGIC_DELETE: {
			const removedCondition = state.displayLogic.displayLogicConditions[action.payload];
			const remainingConditions = state.displayLogic.displayLogicConditions.filter((l) => l.index !== action.payload);
			let indexCount = 0;
			const updatedIndexConditions = remainingConditions.map((c) => ({
				...c,
				index: indexCount++,
			}));

			// have to add now unused attributes back to drop down
			if (isAttributeCondition(removedCondition) && removedCondition.attributes) {
				const removeAttribute = removedCondition.attributes.find((attr) => attr.name === removedCondition.conditionSourceText);

				if (removeAttribute) {
					updatedIndexConditions.forEach((cond) => {
						if (isAttributeCondition(cond) && cond.attributes) {
							cond.attributes = [...cond.attributes, removeAttribute];
						}
					});
				}
			}

			return {
				...state,
				displayLogic: {
					...state.displayLogic,
					displayLogicConditions: updatedIndexConditions,
				},
				unsavedDisplayLogicChanges: true,
			};
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_RESPONSE_CHANGE: {
			const condWithValues = _.cloneDeep(state.displayLogic.displayLogicConditions);
			condWithValues[action.payload.index].displayLogicValues = action.payload.responses.map((r) => ({
				targetValueType: TargetValueType.Response,
				valueText: r,
			}));
			return {
				...state,
				displayLogic: {
					...state.displayLogic,
					displayLogicConditions: condWithValues,
				},
				unsavedDisplayLogicChanges: true,
			};
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_QUESTION_CHANGE: {
			const { surveyQuestionId: selectionId, questionText: selectionText, index: updatingConditionIdx } = action.payload;
			const { displayLogicConditions } = state.displayLogic;

			const updatingCondition = displayLogicConditions[updatingConditionIdx];

			return {
				...state,

				displayLogic: {
					...state.displayLogic,

					displayLogicConditions: displayLogicConditions.map((condition, idx) => {
						if (idx === updatingConditionIdx) {
							return {
								...condition,

								displayLogicValues: [],
								conditionSourceId: selectionId.toString(),
								conditionSourceText: selectionText,
							};
						}

						// If the condition being updated was an attribute based one, then remove the selection option and make the old selection available to all other attribute conditions.
						if (isAttributeCondition(updatingCondition) && isAttributeCondition(condition)) {
							const prevSelectedAttribute = updatingCondition.attributes?.find(
								(attr) => attr.columnIndex.toString() === updatingCondition.conditionSourceId,
							);

							return {
								...condition,

								attributes: condition.attributes?.filter((attr) => attr.columnIndex !== selectionId).concat(prevSelectedAttribute ?? []),
							};
						}

						return condition;
					}),
				},

				unsavedDisplayLogicChanges: true,
			};
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_NODE_SELECTION_CHANGED: {
			const condWithNode = _.cloneDeep([...state.displayLogic.displayLogicConditions]);
			condWithNode[action.payload.index].displayLogicValues = [
				{
					targetValueType: TargetValueType.Response,
					valueText: action.payload.node,
				},
			];
			return {
				...state,
				displayLogic: {
					...state.displayLogic,
					displayLogicConditions: condWithNode,
				},
				unsavedDisplayLogicChanges: true,
			};
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_REQUESTED: {
			const conditionIdx = action.payload;

			return ApplyLogicConditionUpdatesToState(state, conditionIdx, (condition) =>
				DeriveNew(condition, {
					_inherit: true,

					hierarchy: {
						isLoading: true,
					},
				}),
			);
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_FAILED: {
			const conditionIdx = action.payload;

			return ApplyLogicConditionUpdatesToState(state, conditionIdx, (condition) =>
				DeriveNew(condition, {
					_inherit: true,

					hierarchy: {
						isLoading: false,
						couldNotFetchData: true,
					},
				}),
			);
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHY_VM_SUCCEEDED: {
			const { index: conditionIdx, hierarchyVm: hierarchyInfo } = action.payload;

			return ApplyLogicConditionUpdatesToState(state, conditionIdx, (condition) =>
				DeriveNew(condition, {
					_inherit: true,

					hierarchy: {
						...hierarchyInfo,
						isLoading: false,
					},
				}),
			);
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_HIERARCHY_CHANGE: {
			const { hierarchyMetadataId: hierarchyId, name: hierarchyName, displayLogicValues, index: conditionIdx } = action.payload;

			return ApplyLogicConditionUpdatesToState(state, conditionIdx, (condition) =>
				DeriveNew(condition, {
					_inherit: true,

					displayLogicValues,
					conditionSourceId: hierarchyId,
					conditionSourceText: hierarchyName,
				}),
			);
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_REQUESTED:
			return {
				...state,
				selectableHierarchies: { isLoading: true },
			};
		case ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_SUCCEEDED:
			return {
				...state,
				selectableHierarchies: action.payload,
			};
		case ActionTypes.QUESTION_DISPLAY_LOGIC_GET_HIERARCHIES_FAILED:
			return {
				...state,
				selectableHierarchies: { isLoading: false, couldNotFetchData: true },
			};
		case ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_HIERARCHY_CONDITION: {
			const conditionsWithHierarchy = [...state.displayLogic.displayLogicConditions].concat({
				index: state.displayLogic.displayLogicConditions.length,
				displayLogicValues: [],
				conditionSourceType: ConditionSourceType.HierarchyNode,
				conditionType: ConditionType.QuestionResponse,
				targetValueOperator: TargetValueOperator.In,
				hierarchy: {},
			});

			return {
				...state,
				displayLogic: {
					...state.displayLogic,
					displayLogicConditions: conditionsWithHierarchy,
				},
				unsavedDisplayLogicChanges: true,
				errorCondition: undefined,
			};
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_NEW_QUESTION_CONDITION: {
			const newConditions = [...state.displayLogic.displayLogicConditions].concat({
				index: state.displayLogic.displayLogicConditions.length,
				displayLogicValues: [],
				conditionSourceType: ConditionSourceType.PreviousQuestion,
				conditionType: ConditionType.QuestionResponse,
				targetValueOperator: TargetValueOperator.In,
				questions: action.payload,
			});
			return {
				...state,
				displayLogic: {
					...state.displayLogic,
					displayLogicConditions: newConditions,
				},
				unsavedDisplayLogicChanges: true,
				errorCondition: undefined,
			};
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_ADD_NEW_ATTRIBUTE_CONDITION: {
			const newAttributeConditions = [...state.displayLogic.displayLogicConditions].concat({
				index: state.displayLogic.displayLogicConditions.length,
				displayLogicValues: [],
				conditionSourceType: action.payload.sourceType,
				conditionType: ConditionType.QuestionResponse,
				targetValueOperator: TargetValueOperator.In,
				attributes: action.payload.attributes,
			});

			return {
				...state,
				displayLogic: {
					...state.displayLogic,
					displayLogicConditions: newAttributeConditions,
				},
				unsavedDisplayLogicChanges: true,
				errorCondition: undefined,
			};
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_WARNING_MODAL_OPEN:
			return { ...state, showDisplayLogicWarningModal: true };
		case ActionTypes.QUESTION_DISPLAY_LOGIC_WARNING_MODAL_CLOSE:
			return { ...state, showDisplayLogicWarningModal: false };
		case ActionTypes.QUESTION_DISPLAY_LOGIC_SHOW_HIDE_CHANGE:
			return {
				...state,
				displayLogic: {
					...state.displayLogic,
					displayLogicRule: {
						...state.displayLogic.displayLogicRule,
						displayLogicAction: {
							actionType: action.payload,
						},
					},
				},
				unsavedDisplayLogicChanges: true,
			};
		case ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES: {
			const updatedConditions = state.displayLogic.displayLogicConditions.map((cond) => {
				if (isAttributeCondition(cond)) {
					const filteredAttributes = action.payload.filter(
						(attr) =>
							!state.displayLogic.displayLogicConditions.some((c) => c.conditionSourceText === attr.name) ||
							cond.conditionSourceText === attr.name,
					);
					return { ...cond, attributes: filteredAttributes };
				} else {
					return cond;
				}
			});
			return { ...state, attributes: action.payload, displayLogic: { ...state.displayLogic, displayLogicConditions: updatedConditions } };
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTE_VALUES: {
			const conditionsWithAttrValues = state.displayLogic.displayLogicConditions.map((cond) => {
				if (cond.index === action.payload.conditionIndex && isAttributeCondition(cond) && cond.attributes) {
					const attributeValues = new Set<string>();
					action.payload.values.forEach((v) => attributeValues.add(v));

					return {
						...cond,
						displayLogicValues: cond.displayLogicValues?.filter((x) => x.valueText && attributeValues.has(x.valueText)),
						attributes: cond.attributes?.map((attr: IDisplayLogicAttribute) =>
							attr.name === action.payload.name ? { ...attr, values: action.payload.values } : attr,
						),
					};
				} else {
					return { ...cond };
				}
			});

			return {
				...state,
				displayLogic: {
					...state.displayLogic,
					displayLogicConditions: conditionsWithAttrValues,
				},
			};
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_FETCH_ATTRIBUTES_FAILED: {
			const { displayLogicConditions } = state.displayLogic;

			return {
				...state,

				attributes: {
					isLoading: false,
					couldNotFetchData: true,
				},

				displayLogic: {
					...state.displayLogic,

					displayLogicConditions: displayLogicConditions.map((cond) => ({ ...cond, attributes: undefined })),
				},
			};
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_HANDLE_SELECTION_DISMISSAL: {
			const condition = state.displayLogic.displayLogicConditions.find((cond) => cond.index === action.payload.columnIndex);

			if (condition) {
				const updatedCondition = {
					...condition,
					displayLogicValues: condition.displayLogicValues.filter(
						(val) => action.payload.values.findIndex((selected) => selected.name === val.valueText) > -1,
					),
				};
				return {
					...state,
					displayLogic: {
						...state.displayLogic,
						displayLogicConditions: state.displayLogic.displayLogicConditions.map((cond) =>
							cond.index === updatedCondition.index ? updatedCondition : { ...cond },
						),
					},
				};
			} else {
				return { ...state };
			}
		}
		// copied from Hierarchy graph store in common hierarchy
		case ActionTypes.QUESTION_DISPLAY_LOGIC_NODE_TOGGLE: {
			const { nodeId, index: forCondition } = action.payload;

			return ApplyHierarchyUpdatesToState(state, forCondition, (hierarchy) => {
				// If the node that was toggled was not found on the hierarchy state, do not apply any updates.
				if (!hierarchy.nodes?.[nodeId]) {
					return hierarchy;
				}

				const hierarchyNodes = hierarchy.nodes;
				const node = hierarchy.nodes[nodeId];

				const shouldNodeBeExpanded = !node.isExpanded;

				const newHierarchy: IHierarchyVm = {
					...hierarchy,

					nodes: {
						...hierarchyNodes,

						[nodeId]: {
							...node,
							isExpanded: shouldNodeBeExpanded,
						},
					},
				};

				// if collapsing a node, collapse all its children too
				if (!shouldNodeBeExpanded) {
					collapseChildNodes(newHierarchy, nodeId);
				}

				bubbleUpHierarchyNodeUpdates(hierarchy, newHierarchy, nodeId);

				return newHierarchy;
			});
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_NODES_DICT_UPDATE: {
			const { nodeId, hierarchyVm, expandPath, index: forCondition } = action.payload;

			return ApplyHierarchyUpdatesToState(state, forCondition, (hierarchy) => {
				const nodes: HashTable<INodeVm> = { ...hierarchy.nodes };
				const parentIds: HashTable<string> = hierarchy.parentIds ? { ...hierarchy.parentIds } : {};

				// Update nodes hashtable based on the api response
				if (hierarchyVm && hierarchyVm.nodes) {
					for (const item in hierarchyVm.nodes) {
						if (Object.prototype.hasOwnProperty.call(hierarchyVm.nodes, item)) {
							nodes[item] = { ...nodes[item], ...hierarchyVm.nodes[item] };
							nodes[item].childrenIds.forEach((childNodeId) => {
								parentIds[childNodeId] = item;
							});
						}
					}
				}

				// get new state
				const newHierarchy: IHierarchyVm = {
					...hierarchy,
					editInProgress: false,
					parentIds,
					nodes: {
						...nodes,
						[nodeId]: {
							...nodes[nodeId],
							fullyLoaded: true,
						},
					},
				};

				bubbleUpHierarchyNodeUpdates(hierarchy, newHierarchy, nodeId, expandPath);

				if (expandPath) {
					newHierarchy.selectedNodeId = nodeId;
					newHierarchy.pendingSelectedNodeScroll = true;
				}

				return newHierarchy;
			});
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_SELECT_NODE: {
			const { nodeId, index: forCondition } = action.payload;

			return ApplyHierarchyUpdatesToState(state, forCondition, (hierarchy) => {
				const newHierarchy: IHierarchyVm = {
					...hierarchy,
					selectedNodeId: nodeId,
				};

				bubbleUpHierarchyNodeUpdates(hierarchy, newHierarchy, nodeId);

				return newHierarchy;
			});
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_RESET_PENDING_SCROLL: {
			const { index: forCondition } = action.payload;

			return ApplyHierarchyUpdatesToState(state, forCondition, (hierarchy) => ({
				...hierarchy,
				pendingSelectedNodeScroll: undefined,
			}));
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_HIDE_BELONGS_TO_NODES: {
			const { nodeId, index: forCondition } = action.payload;

			return ApplyHierarchyUpdatesToState(state, forCondition, (hierarchy) => {
				// If the node was not found on the hierarchy state, do not apply any updates.
				if (!hierarchy.nodes?.[nodeId]) {
					return hierarchy;
				}

				const hierarchyNodes = hierarchy.nodes;
				const node = hierarchy.nodes[nodeId];

				const newHierarchy: IHierarchyVm = {
					...hierarchy,

					nodes: {
						...hierarchyNodes,

						[nodeId]: {
							...node,
							showBelongsToNodes: false,
						},
					},
				};

				bubbleUpHierarchyNodeUpdates(hierarchy, newHierarchy, nodeId, false);

				return newHierarchy;
			});
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_SHOW_BELONGS_TO_NODES:
		case ActionTypes.QUESTION_DISPLAY_LOGIC_LOAD_MORE_BELONGS_TO_NODES: {
			const { nodes: belongsToNodes, pageRange: belongsToPageRange, nodeId, index: forCondition } = action.payload;

			return ApplyHierarchyUpdatesToState(state, forCondition, (hierarchy) => {
				// If the node was not found on the hierarchy state, do not apply any updates.
				if (!hierarchy.nodes?.[nodeId]) {
					return hierarchy;
				}

				const hierarchyNodes = hierarchy.nodes;
				const node = hierarchyNodes[nodeId];

				const newStateOfNodes: HashTable<INodeVm> = {
					...hierarchyNodes,

					...(belongsToNodes ? belongsToNodes.reduce((nodes, n) => ({ ...nodes, [n.nodeId]: n }), {}) : {}),

					[nodeId]: {
						...node,

						belongsToNodeIds: [...(node.belongsToNodeIds ?? []), ...(belongsToNodes?.map((n) => n.nodeId) ?? [])],
						showBelongsToNodes: true,
						belongsToPageRange: belongsToPageRange,
					},
				};

				const newHierarchy: IHierarchyVm = {
					...hierarchy,
					nodes: newStateOfNodes,
				};

				bubbleUpHierarchyNodeUpdates(hierarchy, newHierarchy, nodeId, false);

				return newHierarchy;
			});
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_SELECT_NODE_UPDATE_COMPLETE: {
			const { hierarchyNodeIds, hierarchyVm, index: forCondition } = action.payload;

			return ApplyHierarchyUpdatesToState(state, forCondition, (hierarchy) => {
				const invalidNodeIds: HashTable<boolean> = {};
				const nodes: HashTable<INodeVm> = { ...hierarchy.nodes };
				const parentIds: HashTable<string> = hierarchy.parentIds ? { ...hierarchy.parentIds } : {};

				// Update nodes hashtable based on the api response
				for (const id in hierarchyNodeIds) {
					if (Object.prototype.hasOwnProperty.call(hierarchyNodeIds, id)) {
						const item = action.payload.hierarchyNodeIds[id];
						const nodesList = hierarchyVm.nodes;

						if (nodesList === undefined) {
							continue;
						}

						if (!(hierarchyNodeIds[id] in nodesList)) {
							invalidNodeIds[item] = true;
						} else {
							nodes[item] = { ...nodesList[item], ...nodes[item] };

							if (nodes[item].childrenIds) {
								nodes[item].childrenIds.forEach((childNodeId) => {
									parentIds[childNodeId] = item;
								});
							}
						}
					}
				}

				return {
					...hierarchy,
					parentIds,
					nodes,
					isLoading: false,
					invalidNodeIds,
				};
			});
		}
		case ActionTypes.QUESTION_DISPLAY_LOGIC_LOAD_MORE_TOP_LEVEL_ORPHAN_NODES: {
			const { nodes, pageRange, index: forCondition } = action.payload;

			return ApplyHierarchyUpdatesToState(state, forCondition, (hierarchy) => {
				const allNodes = { ...hierarchy.nodes };
				const orphanedDependentNodeIdList = [...(hierarchy.orphanedDependentNodeIds?.items ?? [])];

				if (nodes) {
					nodes.forEach((n) => {
						allNodes[n.nodeId] = n;
						orphanedDependentNodeIdList.push(n.nodeId);
					});
				}

				return {
					...hierarchy,
					orphanedDependentNodeIds: {
						items: orphanedDependentNodeIdList,
						pageRange: pageRange,
					},
					nodes: {
						...allNodes,
					},
				};
			});
		}
		default:
			return state;
	}
};

function ApplyLogicConditionUpdatesToState(
	state: IQuestionDisplayLogicModalState,
	forCondition: number,
	performUpdates: (currConditionState: IDisplayLogicCondition) => IDisplayLogicCondition,
) {
	const condition = state.displayLogic.displayLogicConditions[forCondition];

	if (!condition) {
		return state;
	}

	const newCondition = performUpdates(condition);

	return DeriveNew(state, {
		_inherit: true,

		displayLogic: {
			_inherit: true,

			displayLogicConditions: {
				_inherit: true,

				[forCondition]: newCondition,
			},
		},
	});
}

function ApplyHierarchyUpdatesToState(
	state: IQuestionDisplayLogicModalState,
	forCondition: number,
	performUpdates: (currHierarchyState: Omit<IHierarchyVm, 'isLoading'>) => Omit<IHierarchyVm, 'isLoading'>,
) {
	return ApplyLogicConditionUpdatesToState(state, forCondition, (condition) => {
		const { hierarchy } = condition;

		if (!hierarchy || hierarchy.isLoading || hierarchy.couldNotFetchData) {
			return condition;
		}

		const newHierarchy = performUpdates(hierarchy);

		return DeriveNew(condition, {
			_inherit: true,

			hierarchy: newHierarchy,
		});
	});
}

const getFilteredQuestionsFromState = (state: () => IApplicationState, surveyQuestionId?: number) => {
	const { questions, pageFlowElements } = state().survey.surveyEditModel;
	const questionId =
		surveyQuestionId === undefined ? state().questionDisplayLogic.displayLogic.displayLogicRule.surveyQuestionId : surveyQuestionId;
	const sequence = questions.find((q) => q.surveyQuestionId === questionId)!.sequence!;
	return questions
		.filter((q) => q.sequence! < sequence && !isQuestionTypeTextResponse(q.questionType) && !q.parentQuestionId)
		.map((q) => ({
			surveyQuestionId: q.surveyQuestionId!,
			displayOrder: q.displayOrder!,
			text: q.text,
			responseOptionGroups: q.responseOptions!,
			isRequired: q.isRequired,
			pageNumber: getPageNumber(pageFlowElements, q.sequence),
		}));
};

const getPageNumber = (pageFlowElements: IPageFlowElementModel[], questionSequence: number | undefined): number => {
	const nextPage = pageFlowElements
		.filter((elem) => elem.elementType === ElementType.PageBreak)
		.filter((pb) => {
			return (pb.sequence || 0) < (questionSequence || 0);
		});

	if (nextPage.length === 0) {
		return 1;
	}

	return nextPage.length + 1;
};

function getAttributesInUse(state: Readonly<IQuestionDisplayLogicModalState>) {
	const { displayLogicConditions: conditions } = state.displayLogic;

	return new Set(
		conditions
			.filter(isAttributeCondition)
			.map((condition) => condition.conditionSourceId)
			.filter((id) => id),
	);
}

function getUnusedAttributes(state: Readonly<IQuestionDisplayLogicModalState>) {
	const { attributes } = state;

	if (!attributes || attributes.isLoading || attributes.couldNotFetchData) {
		return [];
	}

	const attributesInUse = getAttributesInUse(state);

	return attributes.filter((attr) => !attributesInUse.has(attr.columnIndex.toString()));
}

async function getErrorCondition(state: () => IApplicationState, hierarchyCondition: boolean) {
	// for reference: fileupload is other source
	// Hierarchy is Hierarchy structure
	// Hris is employee file
	const pagedAudiences = await AudienceApi.getPagedAudiences(state().survey.surveyEditModel.survey.surveyId, defaultAudiencePageRange);
	const source = state().survey.surveyEditModel.survey.audienceSourceType;
	if (source === undefined || source === null) {
		return DisplayLogicConditionError.NoSourceSelected;
	} else if (hierarchyCondition && source === AudienceSourceType.FileUpload) {
		return DisplayLogicConditionError.HasOtherSourceAndHierarchySelection;
	} else if (!hierarchyCondition && source === AudienceSourceType.Hierarchy) {
		return DisplayLogicConditionError.HasHierarchySourceAndAttributeSelection;
	} else if (
		!hierarchyCondition &&
		source === AudienceSourceType.FileUpload &&
		(pagedAudiences.items.length === 0 ||
			_.every(
				pagedAudiences.items,
				(item) =>
					item.distributionType === DistributionType.SingleLinkWithoutDistribution || item.distributionType === DistributionType.SingleLink,
			))
	) {
		return DisplayLogicConditionError.HasOtherSourceAndSingleLinkSelection;
	}
	return undefined;
}

const isAttributeCondition = (condition: IDisplayLogicCondition) => {
	return (
		condition.conditionSourceType === ConditionSourceType.HrisAttribute ||
		condition.conditionSourceType === ConditionSourceType.FileAttribute
	);
};

const isNodeFullyLoaded = (hierarchyVm: IHierarchyVm, nodeId: string) => {
	if (!hierarchyVm || !hierarchyVm.nodes) {
		return false;
	}
	const nodeVm: INodeVm = hierarchyVm.nodes[nodeId];
	return nodeVm.fullyLoaded;
};
const getHierarchyMetadataId = (hierarchyVm: IHierarchyVm) => {
	return hierarchyVm.hierarchyHeader!.hierarchyMetadataId;
};

const isQuestionTypeTextResponse = (questionType: QuestionType) => {
	return questionType === QuestionType.OpenEnded || questionType === QuestionType.TextBox;
};
